import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Box,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { formatToUIDateTime, formatToUIDateTimeLong } from "../utils/dateUtils";

interface VitalsHistoryTableProps {
    vitalsHistory: any[];
    handleEditVital: (vital: any) => void;
    readOnly?: boolean;
}

const VitalsHistoryTable: React.FC<VitalsHistoryTableProps> = ({
    vitalsHistory,
    handleEditVital,
    readOnly = false,
}) => {
    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Date & Time
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Weight
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Temp
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Pulse
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Blood Pressure
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Resp
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Blood Sugar
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            SpO2
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Pain
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            LOC
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Meal Portion
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Bowel Movement
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Created By
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Created On
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Updated By
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                            Updated On
                        </TableCell>
                        {!readOnly && (
                            <TableCell
                                align="center"
                                sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}
                            >
                                Actions
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vitalsHistory.filter((v) => v.vitalDate).length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={16} align="center">
                                No vitals history found.
                            </TableCell>
                        </TableRow>
                    ) : (
                        vitalsHistory
                            .filter((vital) => vital.vitalDate)
                            .sort(
                                (a, b) =>
                                    new Date(b.vitalDate).getTime() -
                                    new Date(a.vitalDate).getTime(),
                            )
                            .map((vital, index) => (
                                <TableRow key={vital.id || index}>
                                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 160 }}>
                                        {formatToUIDateTimeLong(vital.vitalDate)}
                                    </TableCell>
                                    <TableCell>
                                        {vital.dailyWeight || vital.weight || "-"}
                                    </TableCell>
                                    <TableCell>{vital.temperature || "-"}</TableCell>
                                    <TableCell>{vital.pulseRate || "-"}</TableCell>
                                    <TableCell>
                                        {vital.dailyBloodPressure || vital.bloodPressure || "-"}
                                    </TableCell>
                                    <TableCell>{vital.respirationRate || "-"}</TableCell>
                                    <TableCell>
                                        {vital.bloodSugar || "-"}
                                    </TableCell>
                                    <TableCell>
                                        {vital.spO2 || vital.oxygenSaturation || "-"}
                                    </TableCell>
                                    <TableCell>{vital.painLevel || "-"}</TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {vital.levelOfConsciousness ||
                                            vital.consciousnessLevel ||
                                            "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {vital.mealPortion || "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {vital.bowelMovement || "-"}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {vital.createdByName || "-"}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 160 }}>
                                        {formatToUIDateTimeLong(
                                            vital.createdDate ||
                                            vital.createdAt ||
                                            vital.created_at
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {vital.updatedByName || "-"}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap", minWidth: 160 }}>
                                        {formatToUIDateTimeLong(
                                            vital.lastModifiedDate ||
                                            vital.updatedAt ||
                                            vital.updated_at
                                        )}
                                    </TableCell>
                                    {!readOnly && (
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditVital(vital)}
                                                    sx={{ minWidth: "auto" }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default VitalsHistoryTable;
